import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { Alert, AlertTitle, Box, type BoxProps, Skeleton } from "@mui/material";
import { ColorComparisonCorner } from "components/dataDisplay/ColorComparisonCorner";
import { ColorPreviewDisplay } from "components/dataDisplay/ColorPreviewDisplay";
import { DigitalSampleImageGallery } from "components/digitalSamples/DigitalSampleImageGallery";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import {
  useDigitalSample,
  useDigitalSampleGalleryImages,
  useImage,
  usePublicDigitalSample,
} from "hooks/digital-samples";
import { usePreviewSpecification } from "hooks/specifications";
import { type FC, useLayoutEffect, useRef, useState } from "react";
import { logCustomEvent } from "../../../datadog";
import { SkeletonImage } from "../../loader/SkeletonImage";
import { Ribbon } from "./Ribbon";

export interface ColoredMaterialPreviewProps extends BoxProps {
  material: ColoredMaterial;
  target?: NamedColor;
  imageType?: "THUMBNAIL" | "SQUARE";
  viewType?: "TABLE" | "GRID";
  isOpenPreviewImage?: boolean;
  onClosePreview?: () => void;
  onOpenDetail?: () => void;
  usePublicEndpoint?: boolean;
}

export const ColoredMaterialPreview: FC<ColoredMaterialPreviewProps> = ({
  material,
  target,
  imageType = "THUMBNAIL",
  viewType = "TABLE",
  isOpenPreviewImage = false,
  onClosePreview,
  onOpenDetail,
  usePublicEndpoint = false,
  ...boxProps
}) => {
  const previewColor = usePreviewSpecification(material);
  const hasDigitalSample = material.digitalSample.hasDigitalSample;
  const filter = { eljExperimentId: material.eljExperimentId };
  const [authSamples, authError, authLoading] = useDigitalSample(
    filter,
    hasDigitalSample && !usePublicEndpoint,
  );
  const [publicSamples, publicError, publicLoading] = usePublicDigitalSample(
    filter,
    hasDigitalSample && usePublicEndpoint,
  );
  const digitalSamples = usePublicEndpoint ? publicSamples : authSamples;
  const error = usePublicEndpoint ? publicError : authError;
  const loading = usePublicEndpoint ? publicLoading : authLoading;
  const image = useImage(digitalSamples, imageType);
  const images = useDigitalSampleGalleryImages(digitalSamples);

  const [height, setHeight] = useState(0);
  const ref = useRef<any>();

  const [open, setOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  const openImageGallery = (idx: number) => {
    logCustomEvent("digital-sample-view", {
      action: "zoom",
      digital_sample_id: images[idx]?.digitalSample?.id,
    });
    if (!isOpenPreviewImage && viewType === "TABLE") {
      setOpen(true);
      setSelectedImage(idx);
    } else {
      onOpenDetail?.();
    }
  };

  const handleOpenDetail = () => {
    onOpenDetail?.();
  };

  const closePreview = () => {
    if (isOpenPreviewImage) {
      onClosePreview?.();
    }
    setOpen(false);
  };

  useLayoutEffect(() => {
    if (hasDigitalSample && images.length > 0) {
      setOpen(isOpenPreviewImage);
    }
  }, [hasDigitalSample, isOpenPreviewImage, images.length]);

  useLayoutEffect(() => {
    setHeight(ref.current.clientHeight);
  }, [setHeight, ref]);

  const ribbonSize = height < 100 ? "small" : "medium";

  return (
    <>
      <Box ref={ref} position="relative" overflow="hidden" {...boxProps}>
        {hasDigitalSample && (
          <>
            {!loading && image && (
              <SkeletonImage
                onClick={() => openImageGallery(0)}
                width="100%"
                height="100%"
                style={{ objectFit: "cover", cursor: "pointer" }}
                src={image.assetUrl}
                alt=""
              />
            )}
            {loading && (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )}
            {error && (
              <Alert sx={{ fontSize: "0.7em" }} severity="error">
                <AlertTitle sx={{ fontSize: "1.1em" }}>
                  Could not load preview image
                </AlertTitle>
                {error.message}
              </Alert>
            )}
            <Ribbon size={ribbonSize} label="AxF" />
          </>
        )}

        {previewColor &&
          (!hasDigitalSample || (!image && !error && !loading)) && (
            <ColorPreviewDisplay
              onClick={() => handleOpenDetail()}
              width={"100%"}
              height={"100%"}
              preview={previewColor}
              alertSize={ribbonSize}
            />
          )}
        {target && !loading && <ColorComparisonCorner target={target} />}
      </Box>
      <DigitalSampleImageGallery
        images={images}
        selectedImage={selectedImage}
        open={open}
        onSelectedImageChange={setSelectedImage}
        onClose={() => closePreview()}
      />
    </>
  );
};
